import { Icon } from "@iconify/react/dist/iconify.js";
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { apiCall } from "../Apis/apiCall";
import useDebounce from "../hook/useDebounce";
import LoadingComponent from "./ui/LoadingComponent";
import TableDataNoAvailable from "./ui/TableDataNoAvailable";
import PaginationControls from "./ui/PaginationControls";
import PerPageDropdown from "./ui/PerPageDropdown";
import CustomSelect from "./ui/CustomSelect";
import { TextWrap } from "./ui/TextWrap";
import SortIcons from "./ui/SortIcons";
import ErrorAlert from "./ui/ErrorAlert";

const ClientBranches = () => {
  const [branches, setBranches] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [clientsLoading, setClientsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedClient, setSelectedClient] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [entriesPerPage, setEntriesPerPage] = useState(10);
  const [totalEntries, setTotalEntries] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [sortConfig, setSortConfig] = useState({
    field: null,
    direction: "asc",
  });
  const debouncedSearchQuery = useDebounce(searchTerm, 500);
  const navigate = useNavigate();

  // Fetch clients for dropdown
  const fetchClients = async () => {
    try {
      setClientsLoading(true);
      const endpoint = `/get-all-clients?page=1&per_page=1000&search=&sort_by=client_id&sort_order=desc`;
      const response = await apiCall("get", endpoint, null, navigate);

      if (response?.data?.success) {
        const clientList = response.data.data || [];
        setClients(clientList);
      } else {
        console.error("Failed to load clients.");
      }
    } catch (error) {
      console.error("Error fetching clients:", error);
    } finally {
      setClientsLoading(false);
    }
  };

  const fetchBranches = async (
    page = 1,
    perPage = 10,
    search = "",
    sortBy = null,
    sortOrder = "asc",
    clientId = ""
  ) => {
    try {
      setLoading(true);
      setErrorMessage("");

      let url = `/get-all-branches-bo?page=${page}&per_page=${perPage}&search=${encodeURIComponent(
        search
      )}`;

      if (sortBy) {
        url += `&sort_by=${sortBy}&sort_order=${sortOrder}`;
      }

      if (clientId) {
        url += `&client_id=${clientId}`;
      }

      const response = await apiCall("get", url, null, navigate);

      if (response?.data?.success) {
        const branchList = response.data.data || [];
        const pagination = response.data.pagination || {};
        const total = pagination.total || branchList.length;
        const currentPageFromResponse = pagination.current_page || page;
        const lastPage =
          pagination.last_page || Math.ceil(total / perPage) || 1;
        const perPageFromResponse = pagination.per_page || perPage;

        setBranches(branchList);
        setTotalEntries(total);
        setCurrentPage(parseInt(currentPageFromResponse, 10));
        setTotalPages(parseInt(lastPage, 10));
        setEntriesPerPage(parseInt(perPageFromResponse, 10));
      } else {
        setErrorMessage("Failed to load branches.");
      }
    } catch (error) {
      console.error("Error fetching branches:", error);
      setErrorMessage(error.message || "Error fetching branches.");
    } finally {
      setLoading(false);
    }
  };

  // Load clients on component mount
  useEffect(() => {
    fetchClients();
  }, []);

  useEffect(() => {
    fetchBranches(
      currentPage,
      entriesPerPage,
      debouncedSearchQuery,
      sortConfig.field,
      sortConfig.direction,
      selectedClient
    );
  }, [
    currentPage,
    entriesPerPage,
    debouncedSearchQuery,
    sortConfig.field,
    sortConfig.direction,
    selectedClient,
  ]);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    setCurrentPage(1);
  };

  const handleClientChange = (selected) => {
    setSelectedClient(selected?.value || "");
    setCurrentPage(1);
  };

  const handleEntriesPerPageChange = (value) => {
    setEntriesPerPage(value);
    setCurrentPage(1);
  };

  const handleSort = (field) => {
    setSortConfig((prev) => ({
      field,
      direction:
        prev.field === field && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxPagesToShow = 5;
    let startPage = Math.max(1, currentPage - 2);
    let endPage = Math.min(totalPages, currentPage + 2);

    if (endPage - startPage < maxPagesToShow - 1) {
      if (startPage === 1) {
        endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);
      } else if (endPage === totalPages) {
        startPage = Math.max(1, endPage - maxPagesToShow + 1);
      }
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }

    return pageNumbers;
  };

  const startEntry =
    totalEntries > 0 ? (currentPage - 1) * entriesPerPage + 1 : 0;
  const endEntry = Math.min(currentPage * entriesPerPage, totalEntries);
  if (errorMessage) {
    return <ErrorAlert />;
  }
  return (
    <div className="p-4 p-md-3 p-sm-2">
      <div className="card shadow-sm">
        <div className="card p-3 p-sm-2 p-md-3">
          <div className="d-flex flex-wrap align-items-center gap-3 mb-3">
            <div
              className="icon-field flex-grow-1"
              style={{ maxWidth: "400px" }}
            >
              <input
                type="text"
                className="form-control form-control-sm"
                placeholder="Search by branch name..."
                value={searchTerm}
                onChange={handleSearchChange}
              />
              <span className="icon">
                <Icon icon="ion:search-outline" />
              </span>
            </div>
            <PerPageDropdown
              perPage={entriesPerPage}
              handlePerPageChange={handleEntriesPerPageChange}
            />
            <div style={{ minWidth: "200px" }}>
              <CustomSelect
                name="client"
                value={selectedClient}
                onChange={(selected) =>
                  setSelectedClient(selected?.value || "")
                }
                options={clients}
                placeholder="Select Client"
                labelKey="client_name"
                valueKey="client_id"
                isLoading={clientsLoading}
                isClearable={true}
                isSearchable={true}
                noOptionsMessage={() => "No clients found"}
              />
            </div>
          </div>

          {errorMessage && (
            <div
              className="alert alert-danger alert-dismissible fade show mb-3"
              role="alert"
            >
              {errorMessage}
              <button
                type="button"
                className="btn-close"
                onClick={() => setErrorMessage("")}
              ></button>
            </div>
          )}

          {branches.length === 0 && !loading ? (
            <TableDataNoAvailable message="No branches found." />
          ) : (
            <div className="position-relative">
              <div
                                className="table-responsive scroll-sm"

                style={{
                  opacity: loading ? 0.4 : 1,
                  pointerEvents: loading ? "none" : "auto",
                  overflowX: "auto",
                }}
              >
                <table className="table bordered-table mb-0 table-hover">
                  <thead>
                    <tr style={{ backgroundColor: "#475EAB", color: "white" }}>
                      <th scope="col" className="p-2">
                        S.No
                      </th>
                      <th
                        scope="col"
                        onClick={() => handleSort("branch_name")}
                        className="p-2"
                        style={{ cursor: "pointer" }}
                      >
                        <div className="d-flex align-items-center justify-content-between">
                          <span>Branch Name</span>
                          <SortIcons
                            field="branch_name"
                            sortConfig={sortConfig}
                          />
                        </div>
                      </th>
                      <th
                        scope="col"
                        onClick={() => handleSort("branch_code")}
                        className="p-2"
                        style={{ cursor: "pointer" }}
                      >
                        <div className="d-flex align-items-center justify-content-between">
                          <span>Branch Code</span>
                          <SortIcons
                            field="branch_code"
                            sortConfig={sortConfig}
                          />
                        </div>
                      </th>
                      <th
                        scope="col"
                        onClick={() => handleSort("address")}
                        className="p-2"
                        style={{ cursor: "pointer" }}
                      >
                        <div className="d-flex align-items-center justify-content-between">
                          <span>Address</span>
                          <SortIcons field="address" sortConfig={sortConfig} />
                        </div>
                      </th>
                      <th
                        scope="col"
                        onClick={() => handleSort("phone_number")}
                        className="p-2"
                        style={{ cursor: "pointer" }}
                      >
                        <div className="d-flex align-items-center justify-content-between">
                          <span>Branch Phone</span>
                          <SortIcons
                            field="phone_number"
                            sortConfig={sortConfig}
                          />
                        </div>
                      </th>
                       <th
                        scope="col"
                        onClick={() => handleSort("company_name")}
                        className="p-2"
                        style={{ cursor: "pointer" }}
                      >
                        <div className="d-flex align-items-center justify-content-between">
                          <span>Company Name</span>
                          <SortIcons
                            field="company_name"
                            sortConfig={sortConfig}
                          />
                        </div>
                      </th>
                      <th
                        scope="col"
                        onClick={() => handleSort("client_name")}
                        className="p-2"
                        style={{ cursor: "pointer" }}
                      >
                        <div className="d-flex align-items-center justify-content-between">
                          <span>Client Name</span>
                          <SortIcons
                            field="client_name"
                            sortConfig={sortConfig}
                          />
                        </div>
                      </th>
                      <th
                        scope="col"
                        onClick={() => handleSort("country_name")}
                        className="p-2"
                        style={{ cursor: "pointer" }}
                      >
                        <div className="d-flex align-items-center justify-content-between">
                          <span>Country</span>
                          <SortIcons
                            field="country_name"
                            sortConfig={sortConfig}
                          />
                        </div>
                      </th>
                      <th
                        scope="col"
                        onClick={() => handleSort("city_name")}
                        className="p-2"
                        style={{ cursor: "pointer" }}
                      >
                        <div className="d-flex align-items-center justify-content-between">
                          <span>City</span>
                          <SortIcons
                            field="city_name"
                            sortConfig={sortConfig}
                          />
                        </div>
                      </th>
                      <th
                        scope="col"
                        onClick={() => handleSort("is_active")}
                        className="p-2"
                        style={{ cursor: "pointer" }}
                      >
                        <div className="d-flex ">
                          <span>Status</span>
                          <SortIcons
                            field="is_active"
                            sortConfig={sortConfig}
                          />
                        </div>
                      </th>
                      <th
                        scope="col"
                        onClick={() => handleSort("created_datetime")}
                        className="p-2"
                        style={{ cursor: "pointer" }}
                      >
                        <div className="d-flex align-items-center justify-content-between">
                          <span>Created Date</span>
                          <SortIcons
                            field="created_datetime"
                            sortConfig={sortConfig}
                          />
                        </div>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {branches.map((branch, index) => (
                      <tr key={branch.branch_id || index}>
                        <td className="p-2">{startEntry + index}</td>
                        <td className="p-2">
                          <Link
                            to={`/branch-location?branch_name=${encodeURIComponent(
                              branch.branch_name
                            )}`}
                            className="text-primary-600"
                          >
                            <TextWrap
                              location={branch.branch_name || "-"}
                              link={true}
                            />
                          </Link>
                        </td>
                        <td className="p-2">{branch.branch_code || "-"}</td>
                        <td className="p-2">
                          <TextWrap location={branch.address || "-"} />
                        </td>
                        <td className="p-2">{branch.phone_number || "-"}</td>
                        <td className="p-2">{branch.company_name || "-"}</td>

                        <td className="p-2">
                          <TextWrap location={branch.client_name || "-"} />
                        </td>
                        <td className="p-2">
                          <Link
                            to={`/all-countries`}
                            className="text-primary-600"
                          >
                            {branch.country_name || "-"}
                          </Link>
                        </td>
                        <td className="p-2">
                          <Link to={`/all-cities`} className="text-primary-600">
                            {branch.city_name || "-"}
                          </Link>
                        </td>
                        <td className="p-2">
                          <span
                            className={`px-3 py-1 radius-4 fw-medium text-sm ${
                              branch.is_active
                                ? "bg-success-focus text-success-600 border border-success-main"
                                : "bg-danger-focus text-danger-600 border border-danger-main"
                            }`}
                          >
                            {branch.is_active ? "Active" : "Inactive"}
                          </span>
                        </td>
                        <td className="p-2">
                          {branch.created_datetime
                            ? new Date(branch.created_datetime).toLocaleString()
                            : "-"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {loading && (
                <div
                  className="position-absolute top-50 start-50 translate-middle d-flex justify-content-center align-items-center"
                  style={{
                    zIndex: 10,
                    borderRadius: "10px",
                    padding: "20px",
                  }}
                >
                  <LoadingComponent />
                </div>
              )}
              <PaginationControls
                pagination={{
                  currentPage,
                  perPage: entriesPerPage,
                  total: totalEntries,
                }}
                handlePageChange={handlePageChange}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ClientBranches;
